import React, { useState, useRef, useCallback, useEffect } from 'react';
import DOMPurify from 'dompurify';
import {
  Building2, Upload, Handshake, Send, ChevronRight, ChevronLeft,
  CheckCircle2, FileText, Palette, Globe, Shield, ArrowRight,
} from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { SignaturePad } from './SignaturePad';
import { toast } from 'sonner';
import styles from './TSPApplicationPage.module.css';

// ─── Types ───
interface StepProps {
  onNext: () => void;
  onBack?: () => void;
}

const TIERS = [
  { value: 'silver', label: 'Silver', discount: '10%', tenants: 'Up to 10', color: '#94a3b8' },
  { value: 'gold', label: 'Gold', discount: '20%', tenants: 'Up to 50', color: '#f59e0b' },
  { value: 'platinum', label: 'Platinum', discount: '30%', tenants: 'Unlimited', color: '#a78bfa' },
];

export function TSPApplicationPage() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Step 1: Company info
  const [companyName, setCompanyName] = useState('');
  const [slug, setSlug] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [description, setDescription] = useState('');
  const [whyResell, setWhyResell] = useState('');
  const [whyTasking, setWhyTasking] = useState('');
  const [companyProfileName, setCompanyProfileName] = useState<string | null>(null);
  const [companyProfileDataUrl, setCompanyProfileDataUrl] = useState<string | null>(null);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [faviconDataUrl, setFaviconDataUrl] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const profileInputRef = useRef<HTMLInputElement>(null);

  // Step 2: Agreement
  const [agreementHtml, setAgreementHtml] = useState('');
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [signerCompanyName, setSignerCompanyName] = useState('');
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [agreementId, setAgreementId] = useState<string | null>(null);
  const [agreementSigned, setAgreementSigned] = useState(false);

  // Step 3: Tier
  const [partnerTier, setPartnerTier] = useState('silver');

  // Load draft from localStorage on mount
  useEffect(() => {
    try {
      const draft = localStorage.getItem('tsp-apply-draft');
      if (draft) {
        const d = JSON.parse(draft);
        if (d.companyName) setCompanyName(d.companyName);
        if (d.slug) setSlug(d.slug);
        if (d.contactEmail) setContactEmail(d.contactEmail);
        if (d.website) setWebsite(d.website);
        if (d.description) setDescription(d.description);
        if (d.whyResell) setWhyResell(d.whyResell);
        if (d.whyTasking) setWhyTasking(d.whyTasking);
        if (d.signerName) setSignerName(d.signerName);
        if (d.signerTitle) setSignerTitle(d.signerTitle);
        if (d.signerCompanyName) setSignerCompanyName(d.signerCompanyName);
        if (d.partnerTier) setPartnerTier(d.partnerTier);
      }
    } catch {}
  }, []);

  // Auto-save draft to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem('tsp-apply-draft', JSON.stringify({
        companyName, slug, contactEmail, website, description, whyResell, whyTasking,
        signerName, signerTitle, signerCompanyName, partnerTier,
      }));
    } catch {}
  }, [companyName, slug, contactEmail, website, description, whyResell, whyTasking, signerName, signerTitle, signerCompanyName, partnerTier]);

  // Load agreement when reaching step 2
  useEffect(() => {
    if (step === 1 && !agreementHtml) {
      const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      setAgreementHtml(getResellerAgreementHtml(today));
      setAgreementLoading(false);
    }
  }, [step, agreementHtml]);

  const handleFileSelect = (file: File, type: 'logo' | 'favicon') => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (type === 'logo') setLogoDataUrl(dataUrl);
      else setFaviconDataUrl(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleProfileSelect = (file: File) => {
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png', 'image/jpeg', 'image/webp'];
    if (!allowed.includes(file.type) && !file.name.match(/\.(pdf|doc|docx|png|jpg|jpeg|webp)$/i)) {
      toast.error('Please upload a PDF, Word document, or image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCompanyProfileDataUrl(reader.result as string);
      setCompanyProfileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleSignAgreement = async () => {
    if (!signerName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!signerCompanyName.trim()) {
      toast.error('Please enter your company name');
      return;
    }
    if (!signatureImage) {
      toast.error('Please sign the agreement');
      return;
    }

    // Sign locally — the signature data will be submitted with the partner application
    const localId = 'pending_' + Date.now();
    setAgreementId(localId);
    setAgreementSigned(true);
    toast.success('Agreement signed successfully');
  };

  const handleSubmitApplication = async () => {
    if (!agreementId) {
      toast.error('Please sign the reseller agreement first');
      return;
    }

    setSubmitting(true);
    try {
      // Single combined call — logo, favicon, and application data all in one
      const res = await fetch('/_api/taskingbot/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _tspApply: true,
          companyName: companyName.trim(),
          slug: slug.trim().toLowerCase(),
          contactEmail: contactEmail.trim(),
          website: website.trim() || undefined,
          description: description.trim() || undefined,
          whyResell: whyResell.trim() || undefined,
          whyTasking: whyTasking.trim() || undefined,
          companyProfile: companyProfileDataUrl || undefined,
          companyProfileName: companyProfileName || undefined,
          partnerTier,
          agreementId,
          logoDataUrl: logoDataUrl || undefined,
          faviconDataUrl: faviconDataUrl || undefined,
          signerName: signerName.trim(),
          signerTitle: signerTitle.trim() || undefined,
          signerCompanyName: signerCompanyName.trim(),
          signatureImage,
        }),
      });

      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as any;
        throw new Error(err.error || 'Failed to submit application');
      }

      setSubmitted(true);
      try { localStorage.removeItem('tsp-apply-draft'); } catch {}
      toast.success('Application submitted!');
    } catch (err: any) {
      toast.error(err.message);
    }
    setSubmitting(false);
  };

  const step1Valid = companyName.trim() && slug.trim() && contactEmail.trim() && whyResell.trim() && whyTasking.trim();
  const step2Valid = agreementSigned;

  if (submitted) {
    return (
      <div className={styles.page}>
        <div className={styles.successCard}>
          <CheckCircle2 size={48} className={styles.successIcon} />
          <h2 className={styles.successTitle}>Application Submitted</h2>
          <p className={styles.successDesc}>
            Your TSP partner application is under review. We'll notify you at{' '}
            <strong>{contactEmail}</strong> once it's approved.
          </p>
          <div className={styles.successDetails}>
            <div className={styles.successRow}>
              <span>Company</span>
              <strong>{companyName}</strong>
            </div>
            <div className={styles.successRow}>
              <span>Slug</span>
              <code>{slug}</code>
            </div>
            <div className={styles.successRow}>
              <span>Tier</span>
              <strong style={{ textTransform: 'capitalize' }}>{partnerTier}</strong>
            </div>
            <div className={styles.successRow}>
              <span>Agreement</span>
              <span className={styles.signedBadge}><CheckCircle2 size={12} /> Signed</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Hero */}
      <div className={styles.tspExplainer}>
        <div className={styles.tspExplainerHeader}>
          <Handshake size={24} />
          <h1 className={styles.tspExplainerTitle}>Ready to be a reseller?</h1>
        </div>
        <p className={styles.tspExplainerText}>
          Become a <strong>Tasking Service Provider (TSP)</strong> — an authorized reseller who distributes
          tasking.tech under your own brand. Get wholesale pricing, white-label branding
          (custom logo, colors, domain), multi-tenant management, and dedicated partner support. You manage
          your own client workspaces while we handle the platform infrastructure.
        </p>
      </div>

      {/* Progress bar */}
      <div className={styles.progressBar}>
        {['Company Info', 'Reseller Agreement', 'Select Tier & Submit'].map((label, i) => (
          <div
            key={i}
            className={`${styles.progressStep} ${i <= step ? styles.progressStepActive : ''} ${i < step ? styles.progressStepDone : ''}`}
            onClick={() => { if (i < step) setStep(i); }}
          >
            <div className={styles.progressDot}>
              {i < step ? <CheckCircle2 size={16} /> : <span>{i + 1}</span>}
            </div>
            <span className={styles.progressLabel}>{label}</span>
          </div>
        ))}
        <div className={styles.progressLine}>
          <div className={styles.progressLineFill} style={{ width: `${(step / 2) * 100}%` }} />
        </div>
      </div>

      {/* Step 1: Company Info + Logo */}
      {step === 0 && (
        <div className={styles.stepCard}>
          <div className={styles.stepHeader}>
            <Building2 size={24} className={styles.stepIcon} />
            <div>
              <h2 className={styles.stepTitle}>Company Information</h2>
              <p className={styles.stepDesc}>Tell us about your organization. Upload your logo for white-label branding.</p>
            </div>
          </div>

          <div className={styles.formGrid}>
            <div className={styles.field}>
              <label className={styles.label}>Company Name <span className={styles.required}>*</span></label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Acme Solutions"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Partner Slug <span className={styles.required}>*</span></label>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="acme-solutions"
              />
              <span className={styles.hint}>
                Your unique identifier: <code>{slug || 'your-slug'}.tasking.tech</code>
              </span>
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Contact Email <span className={styles.required}>*</span></label>
              <Input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="partner@acme.com"
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Website</label>
              <Input
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://acme.com"
              />
            </div>
          </div>

          {/* Logo upload */}
          <div className={styles.logoSection}>
            <h3 className={styles.subTitle}><Upload size={16} /> Brand Assets</h3>
            <p className={styles.hint}>Upload your company logo and favicon for white-label branding. PNG, JPEG, WebP, or SVG. Max 2MB.</p>
            <div className={styles.logoRow}>
              <div className={styles.logoBox}>
                <div
                  className={styles.logoPreview}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoDataUrl ? (
                    <img src={logoDataUrl} alt="Logo preview" />
                  ) : (
                    <div className={styles.logoPlaceholder}>
                      <Upload size={24} />
                      <span>Logo</span>
                    </div>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0], 'logo')}
                />
                <span className={styles.logoLabel}>Company Logo</span>
              </div>
              <div className={styles.logoBox}>
                <div
                  className={styles.faviconPreview}
                  onClick={() => faviconInputRef.current?.click()}
                >
                  {faviconDataUrl ? (
                    <img src={faviconDataUrl} alt="Favicon preview" />
                  ) : (
                    <div className={styles.logoPlaceholder}>
                      <Upload size={16} />
                    </div>
                  )}
                </div>
                <input
                  ref={faviconInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0], 'favicon')}
                />
                <span className={styles.logoLabel}>Favicon</span>
              </div>
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Tell us about your business</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your company, industry, size, and target market..."
              rows={3}
              className={styles.textarea}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Why do you want to resell tasking.tech? <span className={styles.required}>*</span></label>
            <textarea
              value={whyResell}
              onChange={(e) => setWhyResell(e.target.value)}
              placeholder="What's your motivation for becoming a reseller? How does it fit your business model? Who are your target customers?"
              rows={3}
              className={styles.textarea}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>What are the reasons you would like to use tasking.tech? <span className={styles.required}>*</span></label>
            <textarea
              value={whyTasking}
              onChange={(e) => setWhyTasking(e.target.value)}
              placeholder="What features or capabilities attracted you to tasking.tech? How do you plan to use the platform for your clients?"
              rows={3}
              className={styles.textarea}
            />
          </div>

          {/* Company Profile Attachment */}
          <div className={styles.profileUpload}>
            <h3 className={styles.subTitle}><FileText size={16} /> Company Profile</h3>
            <p className={styles.hint}>
              Upload a company profile, pitch deck, or capability statement. PDF, Word, or image. Max 10MB.
            </p>
            <div className={styles.profileRow}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => profileInputRef.current?.click()}
              >
                <Upload size={14} /> {companyProfileName ? 'Replace File' : 'Upload Document'}
              </Button>
              {companyProfileName && (
                <span className={styles.profileFileName}>
                  <FileText size={14} /> {companyProfileName}
                  <button
                    className={styles.profileRemove}
                    onClick={() => { setCompanyProfileName(null); setCompanyProfileDataUrl(null); }}
                    title="Remove"
                  >
                    &times;
                  </button>
                </span>
              )}
            </div>
            <input
              ref={profileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
              hidden
              onChange={(e) => e.target.files?.[0] && handleProfileSelect(e.target.files[0])}
            />
          </div>

          <div className={styles.stepActions}>
            <div />
            <Button onClick={() => setStep(1)} disabled={!step1Valid}>
              Continue <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Reseller Agreement */}
      {step === 1 && (
        <div className={styles.stepCard}>
          <div className={styles.stepHeader}>
            <FileText size={24} className={styles.stepIcon} />
            <div>
              <h2 className={styles.stepTitle}>Reseller Agreement <span style={{ color: 'var(--error, #ef4444)', fontSize: '0.85em' }}>*</span></h2>
              <p className={styles.stepDesc}>Review and sign the TSP Reseller Agreement to continue.</p>
              <p style={{ fontSize: '0.72rem', color: 'var(--muted-foreground, #64748b)', marginTop: '0.25rem', lineHeight: 1.5 }}>
                * This Reseller Agreement constitutes a binding legal instrument between your organization and Deschaine IT LLC (d/b/a Tasking.tech), establishing your appointment as an authorized Tasking.tech Service Provider (TSP). Upon execution, this Agreement grants you the non-exclusive right to market, resell, and distribute access to the Tasking.tech platform to end-customer organizations under your own corporate identity. Your electronic signature below signifies your acknowledgment of, and agreement to be bound by, all terms and conditions set forth herein, including but not limited to: wholesale pricing and payment obligations, first-line support responsibilities, data protection and security requirements, intellectual property restrictions, confidentiality provisions, limitation of liability, and the governing law of the State of Wisconsin. This Agreement shall remain in effect for an initial term of one (1) year, subject to automatic renewal.
              </p>
            </div>
          </div>

          {agreementLoading ? (
            <div className={styles.loading}>Loading agreement...</div>
          ) : (
            <>
              <div
                className={styles.agreementScroll}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(agreementHtml, { ADD_ATTR: ['style', 'target'], ADD_TAGS: ['img'], ALLOW_DATA_ATTR: true }) }}
              />

              {agreementSigned ? (
                <div className={styles.signedNotice}>
                  <CheckCircle2 size={20} />
                  <div>
                    <strong>Agreement Signed</strong>
                    <p>Signed by {signerName}{signerTitle ? `, ${signerTitle}` : ''}{signerCompanyName ? ` — ${signerCompanyName}` : ''}</p>
                  </div>
                </div>
              ) : (
                <div className={styles.signatureSection}>
                  <h3 className={styles.subTitle}><Shield size={16} /> Electronic Signature</h3>
                  <div className={styles.field} style={{ marginBottom: '0.75rem' }}>
                    <label className={styles.label}>Company Name <span className={styles.required}>*</span></label>
                    <Input
                      value={signerCompanyName}
                      onChange={(e) => setSignerCompanyName(e.target.value)}
                      placeholder="Acme Corp, LLC"
                    />
                  </div>
                  <div className={styles.sigFields}>
                    <div className={styles.field}>
                      <label className={styles.label}>Full Name <span className={styles.required}>*</span></label>
                      <Input
                        value={signerName}
                        onChange={(e) => setSignerName(e.target.value)}
                        placeholder="Jane Doe"
                      />
                    </div>
                    <div className={styles.field}>
                      <label className={styles.label}>Title</label>
                      <Input
                        value={signerTitle}
                        onChange={(e) => setSignerTitle(e.target.value)}
                        placeholder="CEO, Partner, etc."
                      />
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label}>Signature <span className={styles.required}>*</span></label>
                    <SignaturePad
                      width={500}
                      height={150}
                      onSignature={(img) => setSignatureImage(img)}
                    />
                  </div>

                  <Button
                    onClick={handleSignAgreement}
                    disabled={!signerName.trim() || !signerCompanyName.trim() || !signatureImage}
                    className={styles.signBtn}
                  >
                    <Shield size={14} /> Sign Agreement
                  </Button>
                </div>
              )}
            </>
          )}

          <div className={styles.stepActions}>
            <Button variant="secondary" onClick={() => setStep(0)}>
              <ChevronLeft size={16} /> Back
            </Button>
            <Button onClick={() => setStep(2)} disabled={!step2Valid}>
              Continue <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Tier Selection + Submit */}
      {step === 2 && (
        <div className={styles.stepCard}>
          <div className={styles.stepHeader}>
            <Handshake size={24} className={styles.stepIcon} />
            <div>
              <h2 className={styles.stepTitle}>Select Partner Tier & Submit</h2>
              <p className={styles.stepDesc}>Choose your partner tier and submit your application for review.</p>
            </div>
          </div>

          <div className={styles.tierGrid}>
            {TIERS.map((t) => (
              <div
                key={t.value}
                className={`${styles.tierCard} ${partnerTier === t.value ? styles.tierCardActive : ''}`}
                onClick={() => setPartnerTier(t.value)}
                style={{ '--tier-color': t.color } as React.CSSProperties}
              >
                <div className={styles.tierHeader}>
                  <span className={styles.tierName} style={{ color: t.color }}>{t.label}</span>
                  {partnerTier === t.value && <CheckCircle2 size={18} style={{ color: t.color }} />}
                </div>
                <div className={styles.tierDiscount}>{t.discount}</div>
                <div className={styles.tierDiscountLabel}>wholesale discount</div>
                <div className={styles.tierTenants}>{t.tenants} tenants</div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className={styles.summary}>
            <h3 className={styles.subTitle}>Application Summary</h3>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryRow}>
                <span>Company</span>
                <strong>{companyName}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Slug</span>
                <code>{slug}</code>
              </div>
              <div className={styles.summaryRow}>
                <span>Email</span>
                <span>{contactEmail}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Tier</span>
                <strong style={{ textTransform: 'capitalize' }}>{partnerTier}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Signer Name</span>
                <strong>{signerName || '—'}</strong>
              </div>
              <div className={styles.summaryRow}>
                <span>Signer Title</span>
                <span>{signerTitle || '—'}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Signer Company</span>
                <span>{signerCompanyName || '—'}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Signed Date</span>
                <span>{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Logo</span>
                <span>{logoDataUrl ? 'Uploaded' : 'None'}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Agreement</span>
                <span className={styles.signedBadge}><CheckCircle2 size={12} /> Signed</span>
              </div>
              {signatureImage && (
                <div className={styles.summaryRow}>
                  <span>Signature</span>
                  <img
                    src={signatureImage}
                    alt="Signature preview"
                    style={{ maxHeight: 50, background: '#fff', border: '1px solid #ccc', borderRadius: 4 }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className={styles.stepActions}>
            <Button variant="secondary" onClick={() => setStep(1)}>
              <ChevronLeft size={16} /> Back
            </Button>
            <Button
              onClick={handleSubmitApplication}
              disabled={submitting}
              className={styles.submitBtn}
            >
              <Send size={14} />
              {submitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function getResellerAgreementHtml(today: string): string {
  return '<div style="font-family: Times New Roman, Georgia, serif; color: #111; line-height: 1.8; font-size: 0.85rem; background: #fff; padding: 2rem;">'
    + '<div style="text-align: center; margin-bottom: 2rem;">'
    + '<h1 style="font-size: 1.25rem; font-weight: 700; margin: 0 0 0.25rem; color: #111; text-transform: uppercase; letter-spacing: 0.05em;">Non-Exclusive SaaS Reseller Agreement</h1>'
    + '<p style="font-size: 0.8rem; color: #555; margin: 0;">Tasking.tech &mdash; Tasking Service Provider (TSP) Program</p>'
    + '<p style="font-size: 0.75rem; color: #777; margin: 0.25rem 0 0;">Version 2.0 &mdash; Effective Date: Date of Reseller&rsquo;s electronic signature</p>'
    + '</div>'
    + '<div style="margin-bottom: 1.5rem; padding: 1rem 1.25rem; background: #f8f9fa; border-left: 3px solid #111; font-size: 0.78rem; color: #333; line-height: 1.7;">'
    + '<p style="margin: 0 0 0.5rem; font-weight: 700; font-size: 0.8rem;">IMPORTANT NOTICE</p>'
    + '<p style="margin: 0;">This document constitutes a legally binding agreement between the undersigned Reseller and Deschaine IT LLC, doing business as Tasking.tech. By affixing your electronic signature hereto, you represent and warrant that: (a) you are duly authorized to execute this Agreement on behalf of the Reseller entity identified below; (b) you have read, understand, and agree to be bound by all terms and conditions contained herein; (c) the Reseller entity is a validly existing legal entity in good standing under the laws of its jurisdiction of formation; and (d) the execution and performance of this Agreement does not violate any other agreement to which the Reseller is a party. This Agreement shall be governed by and construed in accordance with the laws of the State of Wisconsin. If you do not agree to any provision of this Agreement, do not sign.</p>'
    + '</div>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">1. Parties and Background</h2>'
    + '<p>This Non-Exclusive SaaS Reseller Agreement (&ldquo;Agreement&rdquo;) is entered into by and between:</p>'
    + '<p><strong>Company:</strong> Deschaine IT LLC, doing business as Tasking.tech (&ldquo;Company&rdquo;), a Wisconsin limited liability company.</p>'
    + '<p><strong>Reseller:</strong> The entity identified in the electronic signature block below (&ldquo;Reseller&rdquo;).</p>'
    + '<p><strong>Background:</strong> Company operates Tasking.tech, a multi-tenant software-as-a-service (&ldquo;SaaS&rdquo;) platform with per-tenant runtimes and dedicated databases for each customer organization (&ldquo;Tenant&rdquo;). Reseller desires to act as a non-exclusive Tasking.tech Service Provider (&ldquo;TSP&rdquo;), marketing, reselling, and providing access to the platform to multiple customer organizations.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">2. Appointment as Reseller / TSP</h2>'
    + '<p>Company hereby appoints Reseller as a non-exclusive, independent reseller (TSP) with the right to market, promote, and resell subscriptions to the Service to End Customers worldwide, unless restricted by applicable law or a written addendum.</p>'
    + '<p>Reseller may not appoint sub-resellers or sub-distributors without Company&rsquo;s prior written consent. Company reserves all rights not expressly granted herein and may sell the Service directly or through other partners, resellers, or channels.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">3. TSP Program Model and Customer Ownership</h2>'
    + '<p>Reseller, as a TSP, may onboard and manage multiple Customer Tenants within its TSP portal on the Tasking.tech platform. Customers own their data. Company owns the platform and all underlying intellectual property.</p>'
    + '<p><strong>Deal Protection:</strong> If Reseller registers a Customer, Company will not knowingly bypass Reseller to sell the Service directly to that Customer during the term of this Agreement, except where (a) Reseller is terminated for cause, or (b) the Customer explicitly requests a direct relationship with Company.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">4. Pricing, Discounts, and Payment</h2>'
    + '<table style="width: 100%; border-collapse: collapse; margin: 0.75rem 0; font-size: 0.8rem;"><thead><tr style="background: #f5f5f5;"><th style="border: 1px solid #ddd; padding: 0.4rem 0.6rem; text-align: left;">Partner Tier</th><th style="border: 1px solid #ddd; padding: 0.4rem 0.6rem; text-align: left;">Discount off List Price</th></tr></thead><tbody><tr><td style="border: 1px solid #ddd; padding: 0.4rem 0.6rem;">Silver</td><td style="border: 1px solid #ddd; padding: 0.4rem 0.6rem;">30%</td></tr><tr><td style="border: 1px solid #ddd; padding: 0.4rem 0.6rem;">Gold</td><td style="border: 1px solid #ddd; padding: 0.4rem 0.6rem;">40%</td></tr><tr><td style="border: 1px solid #ddd; padding: 0.4rem 0.6rem;">Platinum</td><td style="border: 1px solid #ddd; padding: 0.4rem 0.6rem;">50%</td></tr></tbody></table>'
    + '<p>Company invoices Reseller at the applicable wholesale rate. Reseller sets its own resale pricing. All fees in USD. Payment due within 30 days. Late payments accrue interest at 1.5% per month. Reseller responsible for all applicable taxes.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">5. Orders and Provisioning</h2>'
    + '<p>The Service is a hosted SaaS (cloud) service. Reseller does not receive source code. Reseller resells access rights (subscriptions) only. Tenants are provisioned via the Tasking.tech TSP portal or API.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">6. Marketing and Promotion</h2>'
    + '<p>Reseller shall use reasonable commercial efforts to promote the Service and avoid misleading claims. Reseller may use Company-supplied marketing materials and must obtain approval for custom materials referencing Tasking.tech.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">7. Intellectual Property</h2>'
    + '<p>All IP rights in Tasking.tech remain the exclusive property of Company and its licensors. Reseller receives only the rights expressly granted. No reverse engineering, copying, or creation of competing services.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">8. Branding, Custom Domains, and Logos</h2>'
    + '<p>Reseller may configure custom domains (CNAMEs) and upload TSP-level and per-Tenant logos. These rights do not transfer ownership of Company&rsquo;s trademarks. All brand use ceases upon termination.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">9. Data, Privacy, and Security</h2>'
    + '<p>Customers retain ownership of their data. Company provides multi-tenant security (per-Tenant DBs, RBAC, Zero Trust). Reseller shall comply with all applicable data protection laws and not misuse Customer data.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">10. Compliance and Regulated Environments</h2>'
    + '<p>Certain government or regulated Customers may require separate deployment. Reseller shall not onboard such Customers to the commercial stack without Company&rsquo;s prior written approval.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">11. Support and Service Levels</h2>'
    + '<p>Reseller provides first-line support. Company provides second/third-line platform support with 99.9% uptime target. Reseller may not promise stronger SLAs without assuming responsibility for the difference.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">12. Confidentiality</h2>'
    + '<p>Each party agrees to protect the other&rsquo;s Confidential Information with reasonable care. Standard exclusions apply (already known, independently developed, publicly available, legally compelled).</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">13. Warranties and Disclaimers</h2>'
    + '<p>Company warrants the Service will materially conform to documentation. EXCEPT AS EXPRESSLY SET FORTH HEREIN, COMPANY DISCLAIMS ALL OTHER WARRANTIES TO THE MAXIMUM EXTENT PERMITTED BY LAW.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">14. Indemnification</h2>'
    + '<p>Company indemnifies Reseller against third-party IP infringement claims. Reseller indemnifies Company against claims from misuse, misrepresentation, or breach of data protection laws.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">15. Limitation of Liability</h2>'
    + '<p>NEITHER PARTY SHALL BE LIABLE FOR INDIRECT, CONSEQUENTIAL, OR PUNITIVE DAMAGES. TOTAL LIABILITY CAPPED AT FEES PAID IN THE PRIOR 12 MONTHS. Carve-outs for IP indemnification, confidentiality breach, and willful misconduct.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">16. Term and Termination</h2>'
    + '<p>Initial term: one (1) year, auto-renewing unless 60 days&rsquo; notice. Either party may terminate for material breach not cured within 30 days. Upon termination, Reseller stops marketing and existing Tenants may convert to direct customers.</p>'
    + '<h2 style="font-size: 0.95rem; font-weight: 700; margin: 1.5rem 0 0.5rem; color: #111; border-bottom: 1px solid #ddd; padding-bottom: 0.25rem;">17. Governing Law and Miscellaneous</h2>'
    + '<p><strong>Governing Law:</strong> State of Wisconsin. <strong>Assignment:</strong> Not without consent except in M&amp;A. <strong>Entire Agreement:</strong> This Agreement plus order forms and referenced policies constitutes the entire agreement. Amendments must be in writing.</p>'
    + '<div style="margin-top: 2.5rem; padding-top: 1.5rem; border-top: 2px solid #111;">'
    + '<p style="font-weight: 700; font-size: 0.9rem; text-align: center; margin-bottom: 1.5rem;">IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date.</p>'
    + '<div style="display: flex; gap: 2rem; flex-wrap: wrap;">'
    + '<div style="flex: 1; min-width: 250px;"><p style="font-weight: 700; margin-bottom: 0.5rem;">COMPANY:</p><p>Deschaine IT LLC, d/b/a Tasking.tech</p><div style="margin: 1rem 0; border-bottom: 1px solid #999; padding-bottom: 0.5rem;"><img src="/signatures/company-signature.png" alt="Signature" style="max-height: 60px;" /></div><p><strong>Name:</strong> Ryan Deschaine</p><p><strong>Title:</strong> Owner</p><p><strong>Date:</strong> ' + today + '</p></div>'
    + '<div style="flex: 1; min-width: 250px;"><p style="font-weight: 700; margin-bottom: 0.5rem;">RESELLER:</p><p style="color: #888;">[To be completed upon electronic signature below]</p><div style="margin: 1rem 0; border-bottom: 1px solid #999; padding-bottom: 0.5rem; min-height: 60px;"><em style="color: #999; font-size: 0.75rem;">Reseller signature will appear here</em></div><p><strong>Name:</strong> ________________________________</p><p><strong>Title:</strong> ________________________________</p><p><strong>Date:</strong> ________________________________</p></div>'
    + '</div></div></div>';
}
